import type { EnterpriseStatus } from "../../domain/enterprise";
import type { TaskRecord } from "../tasks/taskEngine";

export type KnowledgeCategory =
  | "Company"
  | "Architecture"
  | "Project"
  | "Product"
  | "Department"
  | "Agent"
  | "Task"
  | "Conversation"
  | "Decision"
  | "Incident";
export type KnowledgeSource =
  "task" | "owner" | "system" | "agent" | "incident";

export type KnowledgeEntry = {
  id: string;
  organizationId: string;
  productId?: string;
  departmentId?: string;
  agentId?: string;
  taskId?: string;
  category: KnowledgeCategory;
  title: string;
  summary: string;
  details: string;
  context: Record<string, unknown>;
  references: string[];
  relationships: string[];
  sources: KnowledgeSource[];
  decisions: string[];
  embeddings: { status: "not-generated"; provider: null };
  confidence: number;
  version: number;
  versionHistory: Array<{
    version: number;
    changedAt: string;
    summary: string;
  }>;
  status: EnterpriseStatus;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  owner: string;
};

export type KnowledgeInput = Omit<
  KnowledgeEntry,
  "id" | "createdAt" | "updatedAt" | "version" | "versionHistory" | "embeddings"
>;

export class KnowledgeEngine {
  private readonly entries = new Map<string, KnowledgeEntry>();

  save(input: KnowledgeInput): KnowledgeEntry {
    const now = new Date().toISOString();
    const entry: KnowledgeEntry = {
      ...input,
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now,
      version: 1,
      versionHistory: [{ version: 1, changedAt: now, summary: input.summary }],
      embeddings: { status: "not-generated", provider: null },
      references: [...input.references],
      relationships: [...input.relationships],
      sources: [...input.sources],
      decisions: [...input.decisions],
      tags: [...input.tags],
      context: { ...input.context },
    };
    this.entries.set(entry.id, entry);
    return entry;
  }

  generateFromTask(task: TaskRecord, owner: string): KnowledgeEntry {
    return this.save({
      organizationId: task.organizationId,
      productId: task.productId,
      departmentId: task.departmentId,
      agentId: task.agentId,
      taskId: task.id,
      category: "Task",
      title: `Task knowledge: ${task.title}`,
      summary: task.description,
      details: task.logs.join("\n") || task.description,
      context: { taskStatus: task.status, dependencies: task.dependencies },
      references: task.memoryLinks,
      relationships: task.dependencies,
      sources: ["task"],
      decisions: [],
      confidence: task.status === "Completed" ? 0.9 : 0.6,
      status: "active",
      tags: ["task", task.status.toLowerCase()],
      owner,
    });
  }

  list(
    filter: { organizationId?: string; productId?: string } = {},
  ): KnowledgeEntry[] {
    return [...this.entries.values()].filter(
      (entry) =>
        (!filter.organizationId ||
          entry.organizationId === filter.organizationId) &&
        (!filter.productId || entry.productId === filter.productId),
    );
  }
}
